import { FC } from "react";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useForm } from "react-hook-form";

import { CoverageEventsFlowStepWrapper } from "@/blocks/CoverageEventsFlowStepWrapper";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useUpdateMedicaidCaseNumbersMutation } from "@/data/mutations/householdMembersMutations";
import { useListHouseholdMembersQuery } from "@/data/queries/householdMembersQueries";
import {
  toScreeningMembers,
  workRequirementCaseNumbersFormSchema,
  WorkRequirementCaseNumbersFormType,
  WorkRequirementType,
} from "@/domain/workRequirements";
import { useWorkRequirementScreeningStep } from "@/hooks/useWorkRequirementScreeningStep";
import { formatHouseholdMemberName } from "@/utils/householdMember";

type CaseNumbersFormPropsType = {
  workRequirement: WorkRequirementType;
  savedCaseNumbers: WorkRequirementCaseNumbersFormType;
};

const CaseNumbersForm: FC<CaseNumbersFormPropsType> = ({
  workRequirement,
  savedCaseNumbers,
}) => {
  const { isSaving, saveAndContinue, goBack } =
    useWorkRequirementScreeningStep();
  const { mutate: updateMedicaidCaseNumbers, isPending: isSavingCaseNumbers } =
    useUpdateMedicaidCaseNumbersMutation();

  const form = useForm<WorkRequirementCaseNumbersFormType>({
    defaultValues: savedCaseNumbers,
    resolver: standardSchemaResolver(workRequirementCaseNumbersFormSchema),
  });

  const { members } = workRequirement;
  const [singleMember] = members;
  const isSingleMember = members.length === 1 && singleMember;
  const singleMemberI18nOptions = isSingleMember
    ? { name: formatHouseholdMemberName(singleMember) }
    : undefined;

  const handleNext = form.handleSubmit(caseNumbers => {
    const continueScreening = () =>
      saveAndContinue({
        lastStep: "CASE_NUMBERS",
        members: toScreeningMembers(workRequirement),
      });

    // Diff against what the form showed: `savedCaseNumbers` follows refetches,
    // so it would overwrite unedited numbers that changed elsewhere.
    const changedCaseNumbers = Object.entries(caseNumbers)
      .filter(
        ([householdMemberId, caseNumber]) =>
          caseNumber !== form.formState.defaultValues?.[householdMemberId],
      )
      .map(([householdMemberId, caseNumber]) => ({
        id: householdMemberId,
        medicaidCaseNumber: caseNumber || null,
      }));

    if (changedCaseNumbers.length === 0) {
      continueScreening();
      return;
    }

    updateMedicaidCaseNumbers(changedCaseNumbers, {
      onSuccess: continueScreening,
    });
  });

  return (
    <CoverageEventsFlowStepWrapper
      onBack={goBack}
      onNext={() => {
        void handleNext();
      }}
      isNextButtonLoading={isSavingCaseNumbers || isSaving}>
      <VnText
        variant="headerTwo"
        i18nKey={
          isSingleMember
            ? "native:workRequirements.caseNumbers.titleSingle"
            : "native:workRequirements.caseNumbers.title"
        }
        i18nOptions={singleMemberI18nOptions}
      />
      <VnText
        variant="lg"
        className="text-muted-foreground mt-2"
        i18nKey={
          isSingleMember
            ? "native:workRequirements.caseNumbers.descriptionSingle"
            : "native:workRequirements.caseNumbers.description"
        }
        i18nOptions={singleMemberI18nOptions}
      />
      <VnText
        variant="lg"
        className="text-muted-foreground mb-6 mt-2"
        i18nKey={
          isSingleMember
            ? "native:workRequirements.caseNumbers.hintSingle"
            : "native:workRequirements.caseNumbers.hint"
        }
        i18nOptions={singleMemberI18nOptions}
      />
      <VnCard>
        <VnForm form={form} isInputOnlyForm>
          {members.map(member => (
            <VnTextInput
              key={member.householdMemberId}
              name={member.householdMemberId}
              control={form.control}
              isRequired={false}
              labelI18nKey={
                isSingleMember
                  ? "native:redeterminationScreen.caseNumberLabel"
                  : "native:workRequirements.caseNumbers.memberLabel"
              }
              labelI18nOptions={{ name: formatHouseholdMemberName(member) }}
              placeholderI18nKey="native:redeterminationScreen.caseNumberPlaceholder"
            />
          ))}
        </VnForm>
      </VnCard>
    </CoverageEventsFlowStepWrapper>
  );
};

export const WorkRequirementCaseNumbersScreen = () => {
  const { workRequirement, isLoading, goBack } =
    useWorkRequirementScreeningStep();
  const { data: householdMembers } = useListHouseholdMembersQuery();

  if (isLoading || !workRequirement || !householdMembers) {
    return (
      <CoverageEventsFlowStepWrapper
        onBack={goBack}
        shouldShowNextButton={false}>
        <VnLoadingSpinner />
      </CoverageEventsFlowStepWrapper>
    );
  }

  const savedCaseNumbers = Object.fromEntries(
    workRequirement.members.map(({ householdMemberId }) => [
      householdMemberId,
      householdMembers.find(({ id }) => id === householdMemberId)
        ?.medicaidCaseNumber ?? "",
    ]),
  );

  return (
    <CaseNumbersForm
      workRequirement={workRequirement}
      savedCaseNumbers={savedCaseNumbers}
    />
  );
};
